import { useState } from 'react';
import { Save, Loader2, Coins, Star } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import useJeMobil from '../../hooks/useJeMobil';
import { SECTION_COLOR } from '../receptura/receptUtils';

/** Výchozí obsah formuláře. U nové ceny dnešek a — když je ceník prázdný —
 *  rovnou primární dodavatel: bez primární ceny nemá kalkulace z čeho počítat. */
const vychozi = (cena, jePrvni) => (cena.id ? {
    ...cena,
    platnost_od: cena.platnost_od?.split('T')[0] || '',
    platnost_do: cena.platnost_do?.split('T')[0] || '',
    je_primarni: !!cena.je_primarni,
} : {
    dodavatel_id: '', cena_bez_dph: '', dph_sazba: 21, cena_jednotka_id: null,
    platnost_od: new Date().toISOString().split('T')[0], platnost_do: '',
    je_primarni: jePrvni,
});

/**
 * Přidání a úprava ceny v ceníku položky — v modalu, ne inline pod seznamem.
 *
 * Inline formulář odsouval ceník nahoru a na telefonu ho vytlačil úplně mimo
 * obrazovku: obsluha zadávala cenu a neviděla, jaké ceny už tam jsou (přesně
 * to, podle čeho se rozhoduje, jestli je nová cena v pořádku). Modal drží
 * ceník na místě a zadávání nad ním.
 *
 * Formulář si drží vlastní kopii dat, takže zavřením se rozdělané změny zahodí
 * a ceník pod ním se nepřekreslí. Zavřený modal se vůbec nemontuje a otevřený
 * dostane `key` podle řádku, který se upravuje — stav se tak nepřenese mezi
 * dvěma po sobě otevřenými cenami a inicializátor `useState` stačí bez efektu.
 *
 * Rám (portál, spodní pozice na mobilu, sklo) řeší `ui/ModalShell`.
 *
 * @param {object|null} cena  null = zavřeno, `{}` = nová cena, jinak úprava
 */
export default function CenaModal({ cena, ...rest }) {
    if (!cena) return null;
    return <Formular key={cena.id ?? 'nova'} cena={cena} {...rest} />;
}

function Formular({
    cena, onClose, onSave, saving = false, chyba = '',
    dodavatele = [], dphSazby = [], altJednotky = [], jednotka, jednotkaId,
    jePrvni = false, isDark,
}) {
    const isMobile = useJeMobil();
    const [form, setForm] = useState(() => vychozi(cena, jePrvni));

    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted, #94a3b8)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${borderColor}`, color: 'var(--text-primary)',
        borderRadius: 6, padding: '9px 12px', fontSize: 13, width: '100%', minWidth: 0, maxWidth: '100%',
        outline: 'none', colorScheme: isDark ? 'dark' : 'light', minHeight: 40,
    };
    const optionStyle = { background: isDark ? '#1e1e2e' : '#fff', color: isDark ? '#e2e8f0' : '#1e293b' };
    // `minWidth: 0` je to podstatné: položka gridu má implicitně `min-width: auto`,
    // takže se nesmrskne pod šířku svého obsahu. Nativní `date` má vlastní
    // minimální šířku („dd.mm.rrrr" + ikona kalendáře), a tou pole ve dvousloupci
    // na telefonu roztlačí buňku — sousední pole se pak přes sebe posunou.
    const pole = (label, obsah) => (
        <div style={{ minWidth: 0 }}>
            <label className="block text-xs font-medium mb-1" style={{ color: secondaryColor }}>{label}</label>
            {obsah}
        </div>
    );

    const zkr = jednotka?.zkratka;
    const alt = form.cena_jednotka_id && form.cena_jednotka_id !== jednotkaId
        && altJednotky.find(a => a.jednotka_do?.id === form.cena_jednotka_id);
    const cenaBez = parseFloat(form.cena_bez_dph);
    const prepocet = alt && alt.koeficient > 0 && cenaBez > 0
        ? { zaZakl: cenaBez / alt.koeficient, sDph: (cenaBez / alt.koeficient) * (1 + form.dph_sazba / 100) }
        : null;

    const lzeUlozit = !!form.dodavatel_id && cenaBez > 0 && !!form.platnost_od;

    const paticka = (
        <div className="flex gap-2">
            <button onClick={onClose} style={{
                flex: 1, minHeight: 44, borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                color: secondaryColor, border: `1px solid ${borderColor}`,
            }}>Zrušit</button>
            <button onClick={() => onSave({ ...form, platnost_do: form.platnost_do || null })}
                disabled={saving || !lzeUlozit}
                style={{
                    flex: 1, minHeight: 44, borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: SECTION_COLOR, color: '#fff', border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    cursor: (saving || !lzeUlozit) ? 'default' : 'pointer',
                    opacity: (saving || !lzeUlozit) ? 0.55 : 1,
                }}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
            </button>
        </div>
    );

    return (
        <ModalShell onClose={onClose} isDark={isDark} ikona={Coins} barva={SECTION_COLOR}
            nadpis={form.id ? 'Úprava ceny' : 'Nová cena'} paticka={paticka}>
                <div className="p-4 space-y-3">
                    {pole('Dodavatel *', (
                        <select value={form.dodavatel_id || ''} onChange={e => setForm({ ...form, dodavatel_id: e.target.value ? parseInt(e.target.value) : '' })} style={{ ...inputStyle, cursor: 'pointer' }}>
                            <option value="" style={optionStyle}>—</option>
                            {dodavatele.map(d => <option key={d.id} value={d.id} style={optionStyle}>{d.nazev}</option>)}
                        </select>
                    ))}

                    {/* Cena a DPH vedle sebe — obojí je krátké číslo a patří k sobě.
                        Jednotka dostane vlastní řádek, ať se popisek převodu vejde. */}
                    <div className="grid grid-cols-2 gap-3">
                        {pole('Cena bez DPH *', <input type="number" step="0.01" inputMode="decimal" autoFocus={!form.id}
                            value={form.cena_bez_dph} onChange={e => setForm({ ...form, cena_bez_dph: e.target.value })} style={inputStyle} />)}
                        {pole('DPH', (
                            <select value={form.dph_sazba} onChange={e => setForm({ ...form, dph_sazba: parseInt(e.target.value) })} style={{ ...inputStyle, cursor: 'pointer' }}>
                                {dphSazby.map(d => <option key={d.sazba} value={d.sazba} style={optionStyle}>{d.nazev}</option>)}
                            </select>
                        ))}
                    </div>

                    {altJednotky.length > 0 && pole('Cena je za', (
                        <select value={form.cena_jednotka_id || ''} onChange={e => setForm({ ...form, cena_jednotka_id: e.target.value ? parseInt(e.target.value) : null })} style={{ ...inputStyle, cursor: 'pointer' }}>
                            <option value="" style={optionStyle}>{zkr} (základní jednotka)</option>
                            {altJednotky.map(aj => <option key={aj.jednotka_do?.id} value={aj.jednotka_do?.id} style={optionStyle}>{aj.jednotka_do?.zkratka} (1 = {aj.koeficient} {zkr})</option>)}
                        </select>
                    ))}

                    {prepocet && (
                        <p className="text-xs px-2 py-2 rounded" style={{ background: isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.08)', color: SECTION_COLOR }}>
                            = {prepocet.zaZakl.toFixed(2)} Kč/{zkr} bez DPH · {prepocet.sDph.toFixed(2)} Kč/{zkr} s DPH
                        </p>
                    )}

                    {/* Data pod sebe, na desktopu vedle sebe. Ve dvou sloupcích zbyde
                        na 360px displeji na pole ~150 px a nativní `date` se do toho
                        nevejde — „dd.mm.rrrr" i s ikonou kalendáře přeteče přes okraj
                        a obě pole se přes sebe posunou. */}
                    <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        {pole('Platnost od *', <input type="date" value={form.platnost_od || ''} onChange={e => setForm({ ...form, platnost_od: e.target.value })} style={inputStyle} />)}
                        {pole('Platnost do', <input type="date" value={form.platnost_do || ''} onChange={e => setForm({ ...form, platnost_do: e.target.value || '' })} style={inputStyle} />)}
                    </div>

                    <label className="flex items-center gap-2 text-xs rounded-lg px-3" style={{
                        color: 'var(--text-primary)', cursor: 'pointer', minHeight: 44,
                        background: form.je_primarni ? 'rgba(245,158,11,0.10)' : 'transparent',
                        border: `1px solid ${form.je_primarni ? SECTION_COLOR + '55' : borderColor}`,
                    }}>
                        <input type="checkbox" checked={!!form.je_primarni} onChange={e => setForm({ ...form, je_primarni: e.target.checked })} />
                        <Star size={13} style={{ color: SECTION_COLOR }} fill={form.je_primarni ? SECTION_COLOR : 'none'} />
                        <span className="font-medium">Primární dodavatel</span>
                        <span style={{ color: mutedColor }}>— z jeho ceny počítá kalkulace</span>
                    </label>

                    {chyba && <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>}
                </div>
        </ModalShell>
    );
}
